<template>
  <div>
    <div class="card-header">
      <div class="card-header-title" style="background-color: white;">
        <div class="title is-6 is-marginless">
          Επεξεργασία Προιόντος
        </div>
      </div>
    </div>
    <div style="background-color: white;">
      <ProductsEditMenu />
      <ToggleableCard :is-open="true" title="Product Pair">
        <template v-slot:info>
          <ul>
            <li>
              <i class="fa fa-check" /> Συνδέστε αυτό το προϊόν με ένα άλλο
              προϊόν
            </li>
            <li>
              <i class="fa fa-check" /> Μπορείτε να αφαιρέσετε το ζευγάρι ανά
              πάσα στιγμή
            </li>
          </ul>
        </template>
        <div class="card-content">
          <!-- Loading State -->
          <div v-if="isLoading" class="notification is-info is-light">
            <p>Φόρτωση συνδεδεμένου προϊόντος...</p>
          </div>

          <!-- Current Pair Display -->
          <div v-else-if="currentPair" class="box">
            <!-- Pair Display Status -->
            <div
              v-if="pairDisplay"
              :class="[
                'notification',
                'is-light',
                'pair-status-banner',
                pairDisplay.display ? 'is-success' : 'is-warning',
              ]"
              style="margin-bottom: 1rem; padding: 0.75rem 1rem;"
            >
              <span class="icon" style="margin-right: 0.5rem;">
                <i
                  :class="
                    pairDisplay.display
                      ? 'fa fa-check-circle'
                      : 'fa fa-exclamation-triangle'
                  "
                />
              </span>
              <span v-if="pairDisplay.display">
                <strong>Εμφανίζεται στο eshop</strong>
              </span>
              <span v-else>
                <strong>Δεν εμφανίζεται στο eshop</strong>
                <span v-if="pairDisplay.reason">
                  &mdash; {{ pairDisplay.reason }}
                </span>
              </span>
            </div>

            <div class="columns is-vcentered">
              <div class="column is-narrow">
                <figure
                  v-if="currentPair.avatar_photo"
                  class="image is-64x64 avatar-container"
                >
                  <img
                    :src="
                      currentPair.avatar_photo.thumbnail ||
                        currentPair.avatar_photo.url
                    "
                    :alt="currentPair.title"
                    class="avatar-img"
                  />
                </figure>
                <figure v-else class="image is-64x64">
                  <div
                    class="has-background-light"
                    style="width: 64px; height: 64px; display: flex; align-items: center; justify-content: center;"
                  >
                    <i
                      class="fa fa-image has-text-grey-light"
                      style="font-size: 24px;"
                    />
                  </div>
                </figure>
              </div>
              <div class="column">
                <p class="title is-6">
                  <RouterLink
                    :to="{
                      name: 'products.edit.pair',
                      params: { uuid: currentPair.uuid },
                    }"
                  >
                    {{ currentPair.title }}
                  </RouterLink>
                </p>
                <p class="subtitle is-7">
                  Common ID: {{ currentPair.common_id }}
                </p>
                <p class="subtitle is-7 has-text-grey">
                  ID: {{ currentPair.id }}
                </p>
              </div>
              <div class="column is-narrow">
                <button
                  type="button"
                  class="button is-danger"
                  @click="handleRemovePair"
                >
                  <span class="icon"><i class="fa fa-trash"/></span>
                  <span>Αφαίρεση</span>
                </button>
              </div>
            </div>
          </div>

          <!-- No Pair Message -->
          <div
            v-else-if="!isLoading && !currentPair"
            class="notification is-info is-light"
          >
            <p>Δεν υπάρχει συνδεδεμένο προϊόν</p>
          </div>

          <!-- Search for New Pair using ProductsSearch component -->
          <div class="field">
            <div class="notification is-light" style="margin-bottom: 1rem;">
              <p class="has-text-weight-bold">
                Προσοχή: Αν επιλέξετε ένα νέο προϊόν, θα αντικαταστήσει το
                τρέχον ζεύγος.
              </p>
              <p>
                Επιλέξτε ένα προϊόν από την αναζήτηση για να συνδέσετε με αυτό
                το προϊόν.
              </p>
            </div>

            <label class="label">Αναζήτηση Προϊόντος για σύνδεση</label>

            <ProductsSearch
              :products="selectedProduct ? [selectedProduct] : []"
              :multiple="false"
              @selectProduct="handleSelectPair"
              @removeProduct="handleClearSelection"
            />
            <p class="help">
              Αναζητήστε και επιλέξτε ένα προϊόν για να το συνδέσετε με το
              τρέχον
            </p>
          </div>
        </div>
      </ToggleableCard>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import request from '@/utils/request';
import ToggleableCard from '@/views/components/ToggleableCard';
import ProductsSearch from '@/views/components/ProductsSearch';
import ProductsEditMenu from '../components/ProductsEditMenu';

export default {
  components: {
    ProductsEditMenu,
    ToggleableCard,
    ProductsSearch,
  },

  props: {
    pairId: {
      type: Number,
      default: null,
    },
    currentProductId: {
      type: Number,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      currentPair: null,
      pairDisplay: null,
      selectedProduct: null,
      isLoading: false,
      productUuid: null,
      actualCurrentProductId: null,
      isSaving: false,
    };
  },

  async created() {
    this.productUuid = this.$route.params.uuid;
    await this.fetchCurrentProduct();
  },

  watch: {
    pairId: {
      handler(newVal) {
        if (newVal) {
          this.fetchPairProduct(newVal);
        } else {
          this.currentPair = null;
        }
      },
      immediate: true,
    },
  },

  methods: {
    async fetchCurrentProduct() {
      try {
        this.isLoading = true;
        const { data } = await request.get(`/products/${this.productUuid}`);

        const product = data.product || data;
        this.actualCurrentProductId = product.id;

        // Store pair display status from API
        this.pairDisplay = data.pair_display || null;

        // If product has a pair_id, fetch it
        if (product.pair_id) {
          await this.fetchPairProduct(product.pair_id);
        }
      } catch (err) {
        console.error('Error fetching current product:', err);
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'Αποτυχία φόρτωσης προϊόντος',
        });
      } finally {
        this.isLoading = false;
      }
    },

    async fetchPairProduct(productId) {
      try {
        this.isLoading = true;
        const { data } = await request.get(`/products-id/${productId}`);

        if (data.product) {
          this.currentPair = data.product;

          if (data.product.photos && data.product.photos.length > 0) {
            const avatarPhoto = data.product.photos.find(p => p.is_avatar);
            if (avatarPhoto) {
              this.currentPair.avatar_photo = {
                url: avatarPhoto.url || avatarPhoto.default_url,
                thumbnail: avatarPhoto.url || avatarPhoto.default_url,
              };
            }
          }
        } else {
          this.currentPair = data;

          if (data.photos && data.photos.length > 0) {
            const avatarPhoto = data.photos.find(p => p.is_avatar);
            if (avatarPhoto) {
              this.currentPair.avatar_photo = {
                url: avatarPhoto.url || avatarPhoto.default_url,
                thumbnail: avatarPhoto.url || avatarPhoto.default_url,
              };
            }
          }
        }
      } catch (err) {
        console.error('Error fetching pair product:', err);
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'Αποτυχία φόρτωσης συνδεδεμένου προϊόντος',
        });
        this.currentPair = null;
      } finally {
        this.isLoading = false;
      }
    },

    async handleSelectPair(product) {
      if (!product) return;

      // Don't allow pairing with itself - use the actual current product ID
      if (product.id === this.actualCurrentProductId) {
        this.$notify({
          type: 'warning',
          title: 'Προσοχή',
          text: 'Δεν μπορείτε να συνδέσετε το προϊόν με τον εαυτό του',
        });
        return;
      }

      // Update the pair in the backend
      await this.updatePairInBackend(product.id);
    },

    async updatePairInBackend(pairId) {
      try {
        this.isSaving = true;

        const { data } = await request.put(
          `/products/${this.actualCurrentProductId}/pair`,
          {
            pair_id: pairId,
          },
        );

        if (data.success) {
          // Store pair display status from update response
          this.pairDisplay = data.pair_display || null;

          // Fetch the paired product to display it
          await this.fetchPairProduct(pairId);

          this.$notify({
            type: 'success',
            title: 'Επιτυχία',
            text: data.message || 'Το ζευγάρι ενημερώθηκε επιτυχώς',
          });
        } else {
          throw new Error(data.message || 'Αποτυχία ενημέρωσης');
        }
      } catch (err) {
        console.error('Error updating pair:', err);
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: err.response?.data?.message || 'Αποτυχία ενημέρωσης ζευγαριού',
        });
      } finally {
        this.isSaving = false;
      }
    },

    async handleRemovePair() {
      try {
        this.isSaving = true;

        const { data } = await request.delete(
          `/products/${this.actualCurrentProductId}/pair`,
        );

        if (data.success) {
          this.currentPair = null;
          this.pairDisplay = null;
          this.selectedProduct = null;

          this.$notify({
            type: 'success',
            title: 'Επιτυχία',
            text: data.message || 'Το ζευγάρι αφαιρέθηκε επιτυχώς',
          });
        } else {
          throw new Error(data.message || 'Αποτυχία αφαίρεσης');
        }
      } catch (err) {
        console.error('Error removing pair:', err);
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: err.response?.data?.message || 'Αποτυχία αφαίρεσης ζευγαριού',
        });
      } finally {
        this.isSaving = false;
      }
    },

    handleClearSelection() {
      this.selectedProduct = null;
    },
  },
};
</script>

<style scoped>
.box {
  background-color: #f9f9f9;
}
.avatar-container {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
}

.avatar-img {
  max-width: 100%;
  max-height: 64px;
  width: auto;
  height: auto;
}

.pair-status-banner {
  display: flex;
  align-items: center;
}
</style>
